import {
    SourceType,
    SourceValidator,
    WebsiteResponse,
    MEDIUM_DOMAIN_REGEX,
    MEDIUM_SUBDOMAIN_REGEX,
    WP_REGEX,
    SnippetOnlyDomains,
    UrlParser
} from "@authory-packages/core";
import * as Yup from "yup";
import { useFormik } from "formik";
import { URL_REGEX } from "../../../utils/prepareURL";
import { V3IconFacebook, V3IconInstagram, V3IconLinkedin, V3IconPodcast, V3IconTwitter, V3IconWeb, V3IconYoutube } from "../../Icons";
import { INVALID_URLS, isValidType, isValidURL, isYoutubeVideo } from "../../../utils/sourceValidation";
import { getSlug } from "../../../utils/getSlug";
import { V3BodyGrey35 } from "../../ReusableStyledComponents/V3BodyGrey35";
import { VerticalSpacing } from "../../VerticalSpacing";
import { InlineLink } from "../../ReusableStyledComponents/InlineLink";

const buildUrlValidation = (sourceType: SourceType) => {
    let validator = Yup.string().trim().ensure();

    validator = validator.matches(URL_REGEX, { message: "Please enter a valid URL" });

    if (sourceType === SourceType.Web) {
        validator = validator
            .test(
                "medium-valid-url",
                "Please enter the URL of your Medium publication, e.g. yourpublication.medium.com, medium.com/yourpublication or medium.com/@username",
                (url) => {
                    if (typeof url !== "string") return true;

                    return SourceValidator.isValidDomainURL(url, "medium.com", [MEDIUM_SUBDOMAIN_REGEX, MEDIUM_DOMAIN_REGEX]);
                }
            )
            .test("wordpress-valid-url", "Please enter the URL of your Wordpress publication, e.g. yourpublication.wordpress.com", (url) => {
                if (typeof url !== "string") return true;

                return SourceValidator.isValidDomainURL(url, "wordpress.com", [WP_REGEX]);
            });
    }

    if (sourceType === SourceType.PodcastRssFeed) {
        validator = validator.test("podcast-valid-url", INVALID_URLS.PODCAST, isValidURL(SourceType.PodcastRssFeed));
    }

    if (sourceType === SourceType.Youtube) {
        validator = validator
            .test("youtube-valid-url", INVALID_URLS.YOUTUBE, isValidURL(SourceType.Youtube))
            .test("youtube-is-video", INVALID_URLS.YOUTUBE, isYoutubeVideo);
    }

    // Validate if user is trying to add a source of a different type
    validator = validator.test("invalid-url-format", INVALID_URLS.SOURCE_MISSMATCH, (value) => {
        const url = value;

        try {
            if (typeof url !== "string") return false;

            // Don't force this validation for podcast sources
            if (sourceType === SourceType.PodcastRssFeed) {
                return true;
            }

            const targetDomain = UrlParser.extractDomain(url);

            if (targetDomain && targetDomain in SnippetOnlyDomains) {
                return true;
            } else {
                return isValidType(sourceType)(value);
            }
        } catch {
            return false;
        }
    });

    return validator;
};

export const getInputUrlIcon = (sourceType: SourceType) => {
    switch (sourceType) {
        case SourceType.Twitter:
            return <V3IconTwitter />;
        case SourceType.Youtube:
            return <V3IconYoutube />;
        case SourceType.Instagram:
            return <V3IconInstagram />;
        case SourceType.Linkedin:
            return <V3IconLinkedin />;
        case SourceType.PodcastRssFeed:
            return <V3IconPodcast />;
        case SourceType.Facebook:
            return <V3IconFacebook />;
        default:
            return <V3IconWeb />;
    }
};

export const getInputLabel = (sourceType: SourceType) => {
    switch (sourceType) {
        case SourceType.Youtube:
            return "YouTube link";
        case SourceType.PodcastRssFeed:
            return "Podcast link";
        case SourceType.Web:
            return "Website URL";
        default:
            return "URL";
    }
};

export const getEditOptionsDescriptionText = (sourceType: SourceType) => {
    switch (sourceType) {
        case SourceType.Youtube:
            return "Please enter a name for this YouTube channel/profile.";
        case SourceType.PodcastRssFeed:
            return "Please enter a name for this podcast series.";
        default:
            return "It looks like you haven't imported anything from this website yet. Please enter a name.";
    }
};

export const getUrlDescriptionText = (sourceType: SourceType, onGoToAddManual: () => void) => {
    switch (sourceType) {
        case SourceType.Youtube:
            return (
                <>
                    Enter the URL of any YouTube channel or profile and Authory will automatically import all of its videos as embeds (Authory will
                    not create backups of these videos). <br /> <br />
                    Please note: Authory will import all of the videos from this channel/profile. If you want to import a select few only, then paste
                    the video URLs <InlineLink onClick={onGoToAddManual}>here</InlineLink>.
                </>
            );
        case SourceType.PodcastRssFeed:
            return (
                <>
                    Please note: Authory will import all of the episodes from this podcast. If you want to import a select few only, then paste the
                    podcast URLs <InlineLink onClick={onGoToAddManual}>here</InlineLink>.
                </>
            );
        default:
            return "Enter the URL of any website where you'd like to import all your articles from. Authory will automatically find and import all pieces with your byline. ✨";
    }
};

export const getInputPlaceholder = (sourceType: SourceType) => {
    switch (sourceType) {
        case SourceType.Youtube:
            return "e.g. http://youtube.com/channel/cambridgeuniversity";
        case SourceType.PodcastRssFeed:
            return "e.g. podcasts.apple.com/us/podcast/equity/id1215439780";
        default:
            return "e.g. https://www.nytimes.com";
    }
};

export const getInputNamePlaceholder = (sourceType: SourceType) => {
    switch (sourceType) {
        case SourceType.Youtube:
            return "e.g. Cambridge University YouTube";
        case SourceType.PodcastRssFeed:
            return "e.g. Up First Podcast";
        default:
            return "e.g. New York Times";
    }
};

export const parseSourceUrl = (value: string) => value.trim();

export type AddSourceFormik = {
    name: string;
    url: string;
    active: boolean;
    type: SourceType;
    importEveryting: boolean;
    somethingElse: boolean;
    aliases: string[];
};

export const useGetAddSourceFormik = (sourceType: SourceType, onSubmit: (data: AddSourceFormik) => Promise<void>, aliases: string[] = []) =>
    useFormik<AddSourceFormik>({
        initialValues: {
            name: "",
            url: "",
            active: true,
            type: sourceType,
            importEveryting: false,
            somethingElse: false,
            aliases: aliases
        },
        onSubmit: onSubmit,
        validationSchema: Yup.object().shape({
            name: Yup.string().max(50, "Name too long (50 characters max)").required("Name is required."),
            url: Yup.string().when("type", ([type]) => buildUrlValidation(type)),
            configuration: Yup.object()
        }),
        enableReinitialize: true
    });

export const findExistingSource = (url: string, sources: WebsiteResponse[]) => {
    const parsedValue = parseSourceUrl(url);
    const newSourceSlug = getSlug(parsedValue);

    let targetSrc: WebsiteResponse | undefined;

    targetSrc = sources.find((src) => {
        try {
            if (src.url) {
                const currentSrcSlug = getSlug(src.url);
                return currentSrcSlug === newSourceSlug;
            }
            return false;
        } catch {
            return false;
        }
    });

    return targetSrc;
};

export enum SourceExistsFormTargets {
    ArticlesMissing = "ArticlesMissing",
    ImportLooksBad = "ImportLooksBad",
    SomethingElse = "SomethingElse"
}

export const getSourceExistsDescription = (target: SourceExistsFormTargets) => {
    switch (target) {
        case SourceExistsFormTargets.ArticlesMissing:
            return (
                <>
                    <strong>Issue:</strong> Not all pieces have been imported.
                    <br />
                    <br />
                    We'd be happy to look into what happened there. Could you please paste the URLs of one or more of any missing pieces as an example
                    below?
                </>
            );
        case SourceExistsFormTargets.ImportLooksBad:
            return (
                <>
                    <strong>Issue:</strong> The previously imported pieces look bad
                    <br />
                    <br />
                    We'd be happy to look into what happened there. Could you let us know what is the problem exactly?
                </>
            );
        case SourceExistsFormTargets.SomethingElse:
            return (
                <>
                    <strong>Issue:</strong> It's something else
                    <br />
                    <br />
                    Please let us know what the issue is, and we'll get back to you shortly. The more detailed the description is, the easier it is
                    for us to help quickly :)
                </>
            );
        default:
            return "";
    }
};

export const getSourceExistsTextAreaPlaceholder = (target: SourceExistsFormTargets) => {
    switch (target) {
        case SourceExistsFormTargets.ArticlesMissing:
            return "e.g. www.nytimes.com/2024/06/07/well/eat/meal-prep-healthy-eating.html";
        case SourceExistsFormTargets.ImportLooksBad:
            return "e.g. imported articles don't contain any images";
        default:
            return "";
    }
};

export enum CompleteTarget {
    "Regular" = "Regular",
    "Troubleshoot" = "Troubleshoot"
}

export const getSourceCompleteTitle = (target: CompleteTarget) => {
    switch (target) {
        case CompleteTarget.Regular:
            return "Import in progress";
        case CompleteTarget.Troubleshoot:
            return "Support request sent";
        default:
            return "";
    }
};

export const getSourceCompleteDescription = (target = CompleteTarget.Regular, collectionName?: string) => {
    switch (target) {
        case CompleteTarget.Regular:
            return (
                <>
                    <VerticalSpacing bottom={12}>
                        <V3BodyGrey35>
                            We are automatically importing your content now. It can take up to 48h, but it's usually faster. We'll email you when the
                            import is ready.
                        </V3BodyGrey35>
                    </VerticalSpacing>
                    <VerticalSpacing bottom={12}>
                        <V3BodyGrey35>
                            We'll also automatically add all future content from this source to your Content Archive
                            {!!collectionName?.length ? `, and to the collection "${collectionName}"` : ""}. ✨
                        </V3BodyGrey35>
                    </VerticalSpacing>
                </>
            );
        case CompleteTarget.Troubleshoot:
            return (
                <>
                    <VerticalSpacing bottom={12}>
                        <V3BodyGrey35>Thanks! We'll look into what happened, and will get back to you via email shortly.</V3BodyGrey35>
                    </VerticalSpacing>
                </>
            );
        default:
            return <></>;
    }
};
